// Settings -> Configuration: platform security of the unified identity platform
// (documentation/identity-architecture.md): authentication providers, MFA policy, roles &
// delegation, outgoing e-mail. Platform administrators with a strong session only; every change is
// audited; no secret is shown or accepted here (credentials are deployment secrets).
import { useCallback, useEffect, useState } from 'react';
import {
  getDelegation, getMail, getMFAPolicy, getProviders, setDelegation, setMail, setMFAPolicy, setProvider, testMail,
  type DelegationRule, type MailRotation, type MailSettings, type MailStatus, type MFASubject, type ProviderSetting,
} from '../../../services/identityPlatform';
import { ApiError } from '../../../services/tenancy';
import { roleLabel, subjectLabel } from '../peopleModel';

export type IdentitySection = 'auth-providers' | 'mfa-policy' | 'delegation' | 'mail';

const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';
const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm w-full';
const card = 'bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3 mb-6';

const PROVIDER_LABELS: Record<string, string> = { radioos: 'RadioOS password (+ authenticator app)', microsoft: 'Microsoft', google: 'Google' };

export function ProvidersPanel({ providers, reachable, busy, onChange }: {
  providers: ProviderSetting[]; reachable: number; busy?: boolean; onChange: (p: ProviderSetting, enabled: boolean, mfaOverride: boolean) => void;
}) {
  const overridden = providers.filter((p) => p.mfa_override);
  return (
    <section className={card}>
      <h3 className="text-lg font-semibold">Authentication providers</h3>
      <p className="text-sm text-gray-400">
        Which sign-in methods RadioOS offers. A Microsoft or Google sign-in counts as multi-factor only with verified evidence in its token;
        otherwise a person whose role requires MFA confirms with the RadioOS authenticator app (set up once, at sign-in or under My account).
        RadioOS passwords use the same authenticator app.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-gray-400"><tr><th scope="col">Provider</th><th scope="col">Deployment</th><th scope="col">Enabled</th><th scope="col">Assume MFA (security override)</th></tr></thead>
        <tbody className="divide-y divide-gray-700">
          {providers.map((p) => (
            <tr key={p.provider}>
              <td className="py-1.5">{PROVIDER_LABELS[p.provider] || p.provider}</td>
              <td className="py-1.5 text-gray-300">{p.configured ? 'Configured' : 'Not configured (no credentials)'}</td>
              <td className="py-1.5">
                <input type="checkbox" aria-label={`${p.provider} enabled`} checked={p.enabled} disabled={busy} onChange={(e) => onChange(p, e.target.checked, p.mfa_override)} />
              </td>
              <td className="py-1.5">
                {p.provider === 'radioos' ? <span className="text-gray-400">Own second factor</span> : (
                  <input type="checkbox" aria-label={`${p.provider} assume MFA (security override)`} checked={p.mfa_override} disabled={busy}
                    onChange={(e) => {
                      if (e.target.checked && !window.confirm(`Assume MFA for EVERY ${p.provider} sign-in without any evidence? Only for a temporary migration; RadioOS cannot verify it.`)) return;
                      onChange(p, p.enabled, e.target.checked);
                    }} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {overridden.length > 0 && (
        <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">
          Security override active for {overridden.map((p) => PROVIDER_LABELS[p.provider] || p.provider).join(', ')}: every sign-in is treated as
          multi-factor although RadioOS cannot verify it. Switch it off once your platform administrators have set up the authenticator app
          (My account); RadioOS refuses the change while it would lock every administrator out.
        </p>
      )}
      <p className={`text-sm ${reachable > 1 ? 'text-gray-300' : 'text-yellow-200'}`}>
        Active platform administrators who can sign in with sufficient assurance: {reachable}. A change that would leave none is refused.
      </p>
    </section>
  );
}

export function MFAPolicyPanel({ policies, busy, onChange }: { policies: MFASubject[]; busy?: boolean; onChange: (s: MFASubject, required: boolean) => void }) {
  const platform = policies.filter((p) => p.scope_type === 'platform');
  const organizations = policies.filter((p) => p.scope_type !== 'platform');
  return (
    <section className={card}>
      <h3 className="text-lg font-semibold">MFA policy</h3>
      <p className="text-sm text-gray-400">
        Whether a sign-in must carry multi-factor authentication. The effective requirement is the strongest of the platform rule and any
        organization rule. Platform roles always require MFA.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-gray-400"><tr><th scope="col">Who</th><th scope="col">MFA</th></tr></thead>
        <tbody className="divide-y divide-gray-700">
          {platform.map((p) => (
            <tr key={p.subject}>
              <td className="py-1.5">{subjectLabel(p.subject)}</td>
              <td className="py-1.5">
                {p.mandatory ? <span className="text-gray-300">Always required</span> : (
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={p.requirement === 'required'} disabled={busy} onChange={(e) => onChange(p, e.target.checked)} />
                    Required
                  </label>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {organizations.length > 0 && (
        <>
          <h4 className="font-semibold text-sm">Stricter organization rules</h4>
          <ul className="text-sm">
            {organizations.map((o) => (
              <li key={`${o.scope_type}:${o.scope_id}:${o.subject}`} className="flex items-center gap-2">
                <span>{o.scope_name || o.scope_id}: {subjectLabel(o.subject)} - required</span>
                <button className="text-xs text-red-300 hover:underline" disabled={busy} onClick={() => onChange(o, false)}>remove</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function DelegationPanel({ rules, busy, onChange }: { rules: DelegationRule[]; busy?: boolean; onChange: (r: DelegationRule) => void }) {
  const actors = Array.from(new Set(rules.map((r) => r.actor_role)));
  const targets = Array.from(new Set(rules.map((r) => r.target_role)));
  const find = (a: string, t: string) => rules.find((r) => r.actor_role === a && r.target_role === t);
  return (
    <section className={card}>
      <h3 className="text-lg font-semibold">Roles &amp; delegation</h3>
      <p className="text-sm text-gray-400">
        Who may give which role, in their own scope. Always, regardless of this table: nobody changes their own access, nobody assigns outside the
        scope they administer or above their own authority, and only platform administrators assign platform roles.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-gray-400">
          <tr><th scope="col">May assign →</th>{targets.map((t) => <th scope="col" key={t}>{roleLabel(t)}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {actors.map((a) => (
            <tr key={a}>
              <th scope="row" className="py-1.5 text-left font-normal">{roleLabel(a)}</th>
              {targets.map((t) => {
                const r = find(a, t);
                return (
                  <td key={t} className="py-1.5">
                    {r ? <input type="checkbox" aria-label={`${roleLabel(a)} may assign ${roleLabel(t)}`} checked={r.allowed} disabled={busy}
                      onChange={(e) => onChange({ ...r, allowed: e.target.checked })} /> : <span className="text-gray-500">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const MAIL_STATE: Record<MailStatus['state'], { label: string; tone: string }> = {
  not_configured: { label: 'Not configured', tone: 'text-yellow-200' },
  configured: { label: 'Configured', tone: 'text-green-300' },
  error: { label: 'Error', tone: 'text-red-300' },
};

/** Safe failure categories (never provider text). */
const MAIL_CATEGORY: Record<string, string> = {
  not_configured: 'not configured',
  auth_failed: 'authentication failed (ids or credential)',
  permission_denied: 'not allowed to send as the sender (permission)',
  sender_unavailable: 'sender mailbox not available',
  rejected: 'rejected by Microsoft Graph',
  throttled: 'throttled by Microsoft Graph',
  transient: 'temporary Microsoft Graph failure',
  timeout: 'no answer in time',
  rate_limited: 'RadioOS send limit reached',
  internal: 'internal error',
};

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '');

const ROTATION_HEALTH: Record<MailRotation['health'], { label: string; tone: string }> = {
  ok: { label: 'Healthy', tone: 'text-green-300' },
  warning: { label: 'Attention', tone: 'text-yellow-200' },
  critical: { label: 'Critical', tone: 'text-red-300' },
  unknown: { label: 'Not reported', tone: 'text-gray-400' },
};

/** The credential type and its automated rotation (public facts only; never key material). */
export function MailCredentialRows({ mail }: { mail: MailStatus }) {
  const rot = mail.rotation;
  const r = rot?.report?.rotation;
  const health = ROTATION_HEALTH[rot?.health || 'unknown'];
  return (
    <>
      <dt className="text-gray-400">Credential type</dt>
      <dd className="col-span-2" data-testid="mail-credential-type">
        {mail.credential_type === 'certificate'
          ? `Certificate (generated on the server; thumbprint ${(mail.credential_thumbprint || '').slice(0, 8)}…)`
          : mail.credential_type === 'client_secret' ? 'Client secret (manual rotation)' : 'Not reported'}
      </dd>
      {mail.credential_not_after && (<><dt className="text-gray-400">Credential expires</dt>
        <dd className="col-span-2">{when(mail.credential_not_after)}{rot?.expires_in_days !== undefined ? ` (${rot.expires_in_days} days)` : ''}</dd></>)}
      <dt className="text-gray-400">Automatic rotation</dt>
      <dd className={`col-span-2 font-semibold ${health.tone}`} data-testid="mail-rotation-health">{health.label}</dd>
      {r && (<>
        <dt className="text-gray-400">Rotation policy</dt>
        <dd className="col-span-2">{r.lifetime_days ? `${r.lifetime_days}-day certificates, rotated ${r.rotate_before_days} days before expiry` : '—'}{r.enabled ? '' : ' (timer not active)'}</dd>
        {r.due_at && (<><dt className="text-gray-400">Next rotation due</dt><dd className="col-span-2">{when(r.due_at)}</dd></>)}
        {r.next_check_at && (<><dt className="text-gray-400">Next check</dt><dd className="col-span-2">{when(r.next_check_at)}</dd></>)}
        {r.last_success_at && (<><dt className="text-gray-400">Last rotation</dt><dd className="col-span-2">{when(r.last_success_at)}</dd></>)}
        {r.last_result && r.last_result !== 'success' && (<><dt className="text-gray-400">Last attempt</dt>
          <dd className="col-span-2 text-red-300">{when(r.last_attempt_at)}: {r.last_result}{r.last_failure_step ? ` at ${r.last_failure_step}` : ''} ({r.failures} consecutive)</dd></>)}
      </>)}
      {rot && rot.reasons.length > 0 && (
        <dd className="col-span-3"><ul className="list-disc pl-5 text-xs text-gray-300" data-testid="mail-rotation-reasons">{rot.reasons.map((x) => <li key={x}>{x}</li>)}</ul></dd>
      )}
    </>
  );
}

export function MailPanel({ mail, busy, onSave, onTest }: {
  mail: MailStatus; busy?: boolean; onSave: (m: MailSettings) => void; onTest: () => void;
}) {
  const [form, setForm] = useState<MailSettings>({
    provider: mail.provider, enabled: mail.enabled, graph_tenant_id: mail.graph_tenant_id, graph_client_id: mail.graph_client_id,
    sender_address: mail.sender_address, sender_name: mail.sender_name || 'RadioOS', link_fallback: mail.link_fallback,
    security_notifications: mail.security_notifications,
  });
  const state = MAIL_STATE[mail.state] || MAIL_STATE.not_configured;
  return (
    <section className={card}>
      <h3 className="text-lg font-semibold">E-mail</h3>
      <p className="text-sm text-gray-400">
        RadioOS sends invitations, password-reset links and security notices through Microsoft Graph, as the sender mailbox, with its own
        mail application (client credentials). "Sent" means Microsoft accepted the message for delivery, not that it reached the mailbox.
      </p>
      <dl className="text-sm grid grid-cols-3 gap-x-3 gap-y-1">
        <dt className="text-gray-400">Status</dt>
        <dd className={`col-span-2 font-semibold ${state.tone}`} data-testid="mail-state">{state.label}</dd>
        <dt className="text-gray-400">Credential</dt>
        <dd className="col-span-2">
          {mail.credential_configured ? 'Configured (installed on the server; never shown)' : `Not configured${mail.credential_error ? ` (${mail.credential_error})` : ''}`}
        </dd>
        {mail.credential_configured && <MailCredentialRows mail={mail} />}
        {mail.last_test_at && (<><dt className="text-gray-400">Last test</dt>
          <dd className="col-span-2">{when(mail.last_test_at)}: {mail.last_test_result === 'sent' ? 'accepted' : MAIL_CATEGORY[mail.last_test_result || ''] || mail.last_test_result}</dd></>)}
        {mail.last_failure_at && (<><dt className="text-gray-400">Last failure</dt>
          <dd className="col-span-2">{when(mail.last_failure_at)}: {MAIL_CATEGORY[mail.last_failure_category || ''] || mail.last_failure_category}</dd></>)}
        {mail.last_sent_at && (<><dt className="text-gray-400">Last accepted</dt><dd className="col-span-2">{when(mail.last_sent_at)}</dd></>)}
      </dl>
      <p className="text-xs text-gray-400 bg-gray-900 border border-gray-700 rounded p-2">{mail.secret_installation}</p>
      <label className="block text-xs text-gray-300" htmlFor="mail-provider">Provider</label>
      <select id="mail-provider" className={input} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value as 'none' | 'graph' })}>
        <option value="none">None (links are shown to the administrator)</option>
        <option value="graph">Microsoft Graph</option>
      </select>
      {form.provider === 'graph' && (
        <>
          <label className="block text-xs text-gray-300" htmlFor="mail-tenant">Directory (tenant) id</label>
          <input id="mail-tenant" className={`${input} font-mono`} value={form.graph_tenant_id} onChange={(e) => setForm({ ...form, graph_tenant_id: e.target.value })} />
          <label className="block text-xs text-gray-300" htmlFor="mail-client">Mail application (client) id</label>
          <input id="mail-client" className={`${input} font-mono`} value={form.graph_client_id} onChange={(e) => setForm({ ...form, graph_client_id: e.target.value })} />
          <label className="block text-xs text-gray-300" htmlFor="mail-sender">Sender mailbox</label>
          <input id="mail-sender" className={input} value={form.sender_address} placeholder="no-reply@example.com" onChange={(e) => setForm({ ...form, sender_address: e.target.value })} />
          <label className="block text-xs text-gray-300" htmlFor="mail-sender-name">Sender name</label>
          <input id="mail-sender-name" className={input} value={form.sender_name} maxLength={100} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Send e-mail</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.security_notifications} onChange={(e) => setForm({ ...form, security_notifications: e.target.checked })} /> Notify people of security changes to their account</label>
        </>
      )}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.link_fallback} onChange={(e) => setForm({ ...form, link_fallback: e.target.checked })} />
        When a message was not sent, show its one-time link once to the administrator who issued it (test / bootstrap; turn off in production)</label>
      <div className="flex gap-2">
        <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy} onClick={() => onSave(form)}>Save configuration</button>
        <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy || !mail.ready} title={mail.ready ? undefined : 'Configure and save first'}
          onClick={onTest}>Test configuration</button>
      </div>
      <p className="text-xs text-gray-400">The test message goes to your own address. Changing the configuration needs a multi-factor sign-in.</p>
    </section>
  );
}

function message(err: unknown): string {
  return err instanceof ApiError ? err.message : 'The request could not be completed. Please try again.';
}

/** Loads and edits one identity section. */
export default function IdentityConfiguration({ section }: { section: IdentitySection }) {
  const [providers, setProviders] = useState<{ providers: ProviderSetting[]; reachable_platform_admins: number } | null>(null);
  const [mfa, setMFA] = useState<MFASubject[] | null>(null);
  const [rules, setRules] = useState<DelegationRule[] | null>(null);
  const [mail, setMailStatus] = useState<MailStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(() => {
    const fail = (e: unknown) => setNote({ ok: false, text: message(e) });
    switch (section) {
      case 'auth-providers': getProviders().then(setProviders, fail); break;
      case 'mfa-policy': getMFAPolicy().then((r) => setMFA(r.policies), fail); break;
      case 'delegation': getDelegation().then(setRules, fail); break;
      case 'mail': getMail().then(setMailStatus, fail); break;
    }
  }, [section]);
  useEffect(() => { setNote(null); load(); }, [load]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      setNote({ ok: true, text: await fn() });
    } catch (e) {
      setNote({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
      load();
    }
  };

  return (
    <div>
      {note && <div role="status" className={`mb-4 rounded p-3 text-sm border ${note.ok ? 'bg-green-900/30 border-green-700 text-green-200' : 'bg-red-900/30 border-red-700 text-red-200'}`}>{note.text}</div>}
      {section === 'auth-providers' && providers && (
        <ProvidersPanel providers={providers.providers} reachable={providers.reachable_platform_admins} busy={busy}
          onChange={(p, enabled, trusted) => run(async () => { await setProvider(p.provider, enabled, trusted); return 'Saved. The change is audited.'; })} />
      )}
      {section === 'mfa-policy' && mfa && (
        <MFAPolicyPanel policies={mfa} busy={busy}
          onChange={(s, required) => run(async () => { await setMFAPolicy({ ...s, requirement: required ? 'required' : 'optional' }); return 'Saved. It applies to the next request of every affected person.'; })} />
      )}
      {section === 'delegation' && rules && (
        <DelegationPanel rules={rules} busy={busy} onChange={(r) => run(async () => { await setDelegation(r); return 'Saved.'; })} />
      )}
      {section === 'mail' && mail && (
        <MailPanel key={JSON.stringify(mail)} mail={mail} busy={busy}
          onSave={(m) => run(async () => { await setMail(m); return 'Saved.'; })}
          onTest={() => run(async () => {
            const r = await testMail();
            if (r.delivery_state === 'sent') return `Test message accepted by Microsoft Graph for ${r.to}.`;
            if (r.delivery_state === 'not_configured') throw new Error('E-mail is not configured.');
            throw new Error(`Sending failed: ${MAIL_CATEGORY[r.delivery_category || ''] || 'error'}. ${r.hint || ''}`);
          })} />
      )}
    </div>
  );
}
